import { createTheme, responsiveFontSizes } from "@mui/material/styles";

// ── Module augmentation for custom palette & shape tokens ──
declare module "@mui/material/styles" {
  interface Palette {
    status: {
      draft: string;
      published: string;
      encoding: string;
      delivered: string;
      archived: string;
    };
  }
  interface PaletteOptions {
    status?: {
      draft?: string;
      published?: string;
      encoding?: string;
      delivered?: string;
      archived?: string;
    };
  }
}

declare module "@mui/system" {
  interface Shape {
    borderRadiusSm: number;
    borderRadiusLg: number;
  }
}

// Create a professional dark theme with vibrant accents
const baseTheme = createTheme({
  palette: {
    mode: "dark",
    // Dark backgrounds for professional feel
    background: {
      default: "#0a0e1a", // Deep dark blue-black
      paper: "#0f1420", // Slightly lighter for cards/surfaces
    },
    // Primary color - Vibrant blue with good contrast
    primary: {
      main: "#3b82f6", // Vibrant blue
      light: "#60a5fa",
      dark: "#2563eb",
      contrastText: "#ffffff",
    },
    // Secondary color - Vibrant purple accent
    secondary: {
      main: "#8b5cf6", // Vibrant purple
      light: "#a78bfa",
      dark: "#7c3aed",
      contrastText: "#ffffff",
    },
    // Error states - Vibrant but serious
    error: {
      main: "#ef4444",
      light: "#f87171",
      dark: "#dc2626",
    },
    // Warning states - Vibrant amber
    warning: {
      main: "#f59e0b",
      light: "#fbbf24",
      dark: "#d97706",
    },
    // Success states - Vibrant emerald
    success: {
      main: "#10b981",
      light: "#34d399",
      dark: "#059669",
    },
    // Info states - Vibrant cyan
    info: {
      main: "#06b6d4",
      light: "#22d3ee",
      dark: "#0891b2",
    },
    // Text colors with good contrast
    text: {
      primary: "#f3f4f6", // Light gray for main text
      secondary: "#9ca3af", // Medium gray for secondary text
      disabled: "#6b7280",
    },
    // Divider color
    divider: "rgba(255, 255, 255, 0.08)",
    // Action colors
    action: {
      active: "#f3f4f6",
      hover: "rgba(255, 255, 255, 0.08)",
      selected: "rgba(59, 130, 246, 0.16)",
      disabled: "rgba(255, 255, 255, 0.3)",
      disabledBackground: "rgba(255, 255, 255, 0.12)",
    },
    // Pipeline status colors
    status: {
      draft: "#64748b", // Slate
      published: "#3b82f6", // Blue
      encoding: "#f59e0b", // Amber
      delivered: "#10b981", // Emerald
      archived: "#334155", // Dark slate
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13, // Base font size reduced
    // Heading styles - Smaller and more compact
    h1: {
      fontWeight: 700,
      fontSize: "1.75rem", // 28px
      letterSpacing: "-0.02em",
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: "1.5rem", // 24px
      letterSpacing: "-0.01em",
      lineHeight: 1.25,
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.25rem", // 20px
      letterSpacing: "-0.01em",
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.125rem", // 18px
      letterSpacing: "0",
      lineHeight: 1.35,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1rem", // 16px
      letterSpacing: "0",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: "0.875rem", // 14px
      letterSpacing: "0",
      lineHeight: 1.4,
    },
    // Body text - Smaller and tighter
    body1: {
      fontSize: "0.875rem", // 14px
      lineHeight: 1.5,
      letterSpacing: "0",
    },
    body2: {
      fontSize: "0.8125rem", // 13px
      lineHeight: 1.5,
      letterSpacing: "0",
    },
    button: {
      fontWeight: 500,
      fontSize: "0.8125rem", // 13px
      letterSpacing: "0.01em",
      textTransform: "none",
    },
    caption: {
      fontSize: "0.75rem", // 12px
      lineHeight: 1.4,
      letterSpacing: "0.01em",
    },
    overline: {
      fontSize: "0.6875rem", // 11px
      lineHeight: 1.5,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    },
    subtitle1: {
      fontSize: "0.875rem", // 14px
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: "0",
    },
    subtitle2: {
      fontSize: "0.8125rem", // 13px
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: "0",
    },
  },
  shape: {
    borderRadius: 8, // Slightly rounded for modern feel
    borderRadiusSm: 4,
    borderRadiusLg: 12,
  },
  shadows: [
    "none",
    "0px 2px 4px rgba(0,0,0,0.4)",
    "0px 4px 8px rgba(0,0,0,0.4)",
    "0px 8px 16px rgba(0,0,0,0.4)",
    "0px 12px 24px rgba(0,0,0,0.4)",
    "0px 16px 32px rgba(0,0,0,0.4)",
    "0px 20px 40px rgba(0,0,0,0.4)",
    "0px 24px 48px rgba(0,0,0,0.4)",
    "0px 28px 56px rgba(0,0,0,0.4)",
    "0px 32px 64px rgba(0,0,0,0.4)",
    "0px 36px 72px rgba(0,0,0,0.4)",
    "0px 40px 80px rgba(0,0,0,0.4)",
    "0px 44px 88px rgba(0,0,0,0.4)",
    "0px 48px 96px rgba(0,0,0,0.4)",
    "0px 52px 104px rgba(0,0,0,0.4)",
    "0px 56px 112px rgba(0,0,0,0.4)",
    "0px 60px 120px rgba(0,0,0,0.4)",
    "0px 64px 128px rgba(0,0,0,0.4)",
    "0px 68px 136px rgba(0,0,0,0.4)",
    "0px 72px 144px rgba(0,0,0,0.4)",
    "0px 76px 152px rgba(0,0,0,0.4)",
    "0px 80px 160px rgba(0,0,0,0.4)",
    "0px 84px 168px rgba(0,0,0,0.4)",
    "0px 88px 176px rgba(0,0,0,0.4)",
    "0px 92px 184px rgba(0,0,0,0.4)",
  ],
  spacing: 6, // Reduced from default 8px to 6px for more compact layout
  components: {
    // Global density settings
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true,
      },
    },
    // Button customization - more compact
    MuiButton: {
      defaultProps: {
        size: "small", // Default to small size
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: "4px 12px",
          fontWeight: 500,
          fontSize: "0.8125rem",
          minHeight: 32,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
          },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            "&:hover": { transform: "none" },
          },
          "@media (max-width: 900px)": {
            minHeight: 48,
          },
        },
        sizeMedium: {
          padding: "6px 16px",
          minHeight: 36,
        },
        sizeLarge: {
          padding: "8px 20px",
          minHeight: 40,
        },
        contained: {
          background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          "&:hover": {
            background: "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)",
            boxShadow: "0 4px 16px rgba(59, 130, 246, 0.4)",
          },
        },
        outlined: {
          borderWidth: 1,
          "&:hover": {
            borderWidth: 1,
            backgroundColor: "rgba(59, 130, 246, 0.08)",
          },
        },
      },
    },
    // Paper customization for cards with gradients
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "linear-gradient(135deg, #0f1420 0%, #141824 100%)",
          backgroundColor: "transparent",
          border: "1px solid rgba(255, 255, 255, 0.05)",
        },
        elevation1: {
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.4)",
        },
        elevation2: {
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.4)",
        },
        elevation3: {
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
        },
      },
    },
    // AppBar customization with gradient
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: "linear-gradient(90deg, #0f1420 0%, #141824 50%, #0f1420 100%)",
          backgroundColor: "transparent",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.4)",
          minHeight: 48,
        },
      },
    },
    // TextField customization - more compact
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            fontSize: "0.8125rem",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.04)",
            },
            "&.Mui-focused": {
              backgroundColor: "rgba(255, 255, 255, 0.06)",
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
          },
        },
      },
    },
    // Form control - more compact
    MuiFormControl: {
      defaultProps: {
        size: "small",
        margin: "dense",
      },
    },
    // Input - smaller
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
        },
        inputSizeSmall: {
          padding: "6px 10px",
        },
      },
    },
    // Table customization - more compact
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          padding: "6px 12px",
          fontSize: "0.8125rem",
        },
        head: {
          background: "linear-gradient(180deg, #0a0e1a 0%, #0f1420 100%)",
          fontWeight: 600,
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          padding: "8px 12px",
        },
        sizeSmall: {
          padding: "4px 8px",
        },
      },
    },
    // Drawer customization with gradient
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: "linear-gradient(180deg, #0f1420 0%, #0a0e1a 100%)",
          backgroundColor: "transparent",
          borderRight: "1px solid rgba(255, 255, 255, 0.08)",
        },
      },
    },
    // Chip customization - more compact
    MuiChip: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 500,
          fontSize: "0.75rem",
          height: 24,
        },
        sizeSmall: {
          height: 20,
        },
        filled: {
          background: "linear-gradient(135deg, rgba(59, 130, 246, 0.16) 0%, rgba(139, 92, 246, 0.16) 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, rgba(59, 130, 246, 0.24) 0%, rgba(139, 92, 246, 0.24) 100%)",
          },
        },
      },
    },
    // Card customization with gradient
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "linear-gradient(145deg, #0f1420 0%, #141824 100%)",
          backgroundColor: "transparent",
          border: "1px solid rgba(255, 255, 255, 0.05)",
          borderRadius: 8,
          padding: "12px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 24px rgba(59, 130, 246, 0.15)",
            borderColor: "rgba(59, 130, 246, 0.3)",
            backgroundImage: "linear-gradient(145deg, #141824 0%, #0f1420 100%)",
          },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            "&:hover": { transform: "none" },
          },
        },
      },
    },
    // CardContent - more compact
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "12px",
          "&:last-child": {
            paddingBottom: "12px",
          },
        },
      },
    },
    // Dialog customization with gradient
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: "linear-gradient(135deg, #0f1420 0%, #141824 100%)",
          backgroundColor: "transparent",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 16px 48px rgba(0, 0, 0, 0.6)",
          "@media (max-width: 600px)": {
            maxWidth: "95vw",
            margin: 16,
          },
        },
      },
    },
    // DialogContent - more compact
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "16px",
          "@media (max-width: 600px)": {
            padding: "12px",
          },
        },
      },
    },
    // DialogActions - more compact
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "8px 16px",
        },
      },
    },
    // Tab customization - more compact
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.8125rem",
          minHeight: 36,
          padding: "6px 12px",
          "&.Mui-selected": {
            color: "#3b82f6",
            background: "linear-gradient(180deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.04) 100%)",
          },
        },
      },
    },
    // Tabs - more compact
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 36,
        },
      },
    },
    // Alert customization - more compact with gradients
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 500,
          fontSize: "0.8125rem",
          padding: "4px 12px",
        },
        standardSuccess: {
          background: "linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)",
          color: "#34d399",
          "& .MuiAlert-icon": {
            color: "#10b981",
            fontSize: "1.125rem",
          },
        },
        standardError: {
          background: "linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(239, 68, 68, 0.08) 100%)",
          color: "#f87171",
          "& .MuiAlert-icon": {
            color: "#ef4444",
            fontSize: "1.125rem",
          },
        },
        standardWarning: {
          background: "linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(245, 158, 11, 0.08) 100%)",
          color: "#fbbf24",
          "& .MuiAlert-icon": {
            color: "#f59e0b",
            fontSize: "1.125rem",
          },
        },
        standardInfo: {
          background: "linear-gradient(135deg, rgba(6, 182, 212, 0.12) 0%, rgba(6, 182, 212, 0.08) 100%)",
          color: "#22d3ee",
          "& .MuiAlert-icon": {
            color: "#06b6d4",
            fontSize: "1.125rem",
          },
        },
      },
    },
    // List items - more compact
    MuiListItem: {
      styleOverrides: {
        root: {
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          paddingTop: 6,
          paddingBottom: 6,
          fontSize: "0.8125rem",
          "&:hover": {
            background: "linear-gradient(90deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.04) 100%)",
          },
          "@media (max-width: 900px)": {
            minHeight: 48,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: "0.8125rem",
        },
        secondary: {
          fontSize: "0.75rem",
        },
      },
    },
    // Toolbar - more compact
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: "48px !important",
          "@media (min-width: 600px)": {
            minHeight: "48px !important",
          },
        },
      },
    },
  },
});

// Apply responsive font sizes — adds @media breakpoints to typography variants
export const darkTheme = responsiveFontSizes(baseTheme);

// Export light theme for comparison/toggle
export const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#3b82f6",
    },
    secondary: {
      main: "#8b5cf6",
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});